<template>
  <div class="order-detail-layout" v-if="order">
    <!-- Main content area -->
    <div class="order-detail-content">
      <div class="card p-0 m-0">
        <div class="card-body p-0 m-0">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center ms-2 mt-2">
              <h4 class="card-title mb-0">Orderdetailpagina</h4>
            </div>
            <div class="m-2 d-flex align-items-center">
              <!-- PDF toggle button - only shown when PDF is available -->
              <button v-if="hasPdfFile" 
                type="button" 
                class="btn btn-sm me-2"
                :class="showPdfViewer ? 'btn-primary' : 'btn-outline-primary'"
                @click="showPdfViewer = !showPdfViewer"
                :title="showPdfViewer ? 'Verberg PDF' : 'Toon PDF'"
              >
                <i class="bi bi-file-pdf me-1"></i>
                <span class="d-none d-md-inline">{{ showPdfViewer ? 'Verberg PDF' : 'Toon PDF' }}</span>
              </button>
              <!-- Bewaar als nieuw knop voor orders met status "Dubbel orderaanbod" -->
              <button v-if="isDuplicateOrder && isOrderEditable"
                type="button" @click="saveAsNew()" class="btn btn-primary me-2">
                <i class="bi bi-save me-1"></i>
                <span class="d-none d-md-inline">Bewaar als nieuw</span>
              </button>
              <button v-if="isOrderLocked" type="button" @click="unlockOrder()" class="btn btn-sm btn-outline-primary ms-3" title="Ontgrendel deze order voor handmatige bewerking">
                <i class="bi bi-unlock me-1"></i>
                <span class="d-none d-md-inline">Ontgrendel</span>
              </button>
              <button v-if="isAdmin" type="button" @click="deleteOrder()" class="btn btn-sm btn-primary ms-3">
                <i class="bi bi-trash me-1"></i>
                <span class="d-none d-md-inline">Verwijder</span>
              </button>
            </div>
          </div>
          <div class="row p-0 ms-0 me-2">
            <div class="col-12 p-1 m-0 d-flex justify-content-between align-items-center">
              <h3>{{ unit4OrganisationName || (order?.customer?.organisation_name ? order?.customer?.organisation_name : order?.customer?.name + '*') }}{{ getHeader(order?.reference) }}{{ getHeader(order?.vo_nummer) }}{{ getHeader(getFormatedDateTime(order?.updated_at)) }}</h3>
            </div>
          </div>
          <div class="row p-0 m-0">
        <div class="col-lg-3 col-md-4 mt-2 mb-2">
          <div class="d-flex align-items-center">
            <i class="bi bi-circle-fill me-2" :class="getColor(order?.status?.id)"></i>
            <select v-if="order?.status" v-model="order.status.id" @change="updateOrderStatus" class="form-select" :disabled="isStatusLocked">
              <option v-for="status in orderstatussen" :key="status.id" :value="status.id">
                {{ status.label }}
              </option>
            </select>
          </div>
          <div v-if="!order?.history || order?.history?.length == 0" v-html="order?.error_message"></div>
          <div v-for="history in sortedHistory" :key="history.id" class="mt-2 small-text">
            <b>{{ getFormattedDate(history.created_at) }}</b> - <span v-html="history.error_message"></span>
            <ul v-if="history.line_errors && history.line_errors.length" class="mt-1 ms-3 mb-0 line-errors">
              <li v-for="(error, index) in history.line_errors" :key="index">
                <span v-html="error.message"></span>
              </li>
            </ul>
          </div>
        </div>
        <div class="col-lg-9 col-md-8 mb-3 py-2">
          <OrderDetailTab @click="select"></OrderDetailTab>
          <div class="border no-top-border p-3 custom-border-radius"> 
            <div class="row" v-if="selectedView == 'object'">
              <div class="d-flex justify-content-end align-items-center mb-2">
                <template v-if="isOrderEditable">
                  <button type="button"
                    class="btn btn-outline-secondary btn-sm me-2"
                    :disabled="!canUndo || isAutoSaving"
                    @click="undoChange"
                    title="Vorige wijziging">
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                  <button type="button"
                    class="btn btn-outline-secondary btn-sm me-2"
                    :disabled="!canRedo || isAutoSaving"
                    @click="redoChange"
                    title="Volgende wijziging">
                    <i class="bi bi-arrow-clockwise"></i>
                  </button>
                  <span v-if="isAutoSaving" class="me-2 text-muted small">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Opslaan...
                  </span>
                  <button v-if="(isAdmin || canManageOrderLines) && hasDeletedOrderLines"
                    type="button" @click="resetOrderLines()" class="btn btn-outline-secondary btn-sm me-2">
                    <i class="bi bi-arrow-counterclockwise me-1"></i>
                    Herstel orderregels
                  </button>
                  <button type="button"
                    class="btn btn-warning me-2"
                    :disabled="isAutoSaving"
                    :title="isAutoSaving ? 'Wacht tot wijzigingen opgeslagen zijn' : 'Verzend de order handmatig naar Unit4'"
                    @click="retryOrderWithJson()">
                    <i class="bi bi-arrow-repeat me-1"></i>
                    Handmatig versturen
                  </button>
                </template>
                <!-- Toggle voor de Unit4-orderstatus: Onderhanden (O) ↔ Wordt ingegeven (B).
                     Alleen voor admins en alleen als de order al in Unit4 is aangemaakt (vo_nummer).
                     B→O is enabled wanneer de RTD-voorraad (Rotterdam, warehouse 01) >= de bestelde
                     hoeveelheid is (ELS telt niet mee); O→B (terugzetten) mag altijd. Bij andere
                     statussen (bv. Afgehandeld) of tijdens laden is de knop disabled. -->
                <button v-if="isAdmin && order?.vo_nummer"
                  type="button"
                  class="btn me-2"
                  :class="isOnderhanden ? 'btn-outline-secondary' : 'btn-success'"
                  :disabled="onderhandenButtonDisabled"
                  :title="onderhandenButtonTitle"
                  @click="toggleUnit4Status()">
                  <span v-if="isSettingUnit4Status" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <i v-else class="bi me-1" :class="isOnderhanden ? 'bi-arrow-counterclockwise' : 'bi-hourglass-split'"></i>
                  {{ onderhandenButtonLabel }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary py-1 px-2"
                  @click="copyObjectToClipboard()">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
              <div class="orderline-header">
                <div class="card mb-2">
                  <div class="card-body">
                    <h4 class="card-title">Order</h4>
                      <div class="row">
                        <div class="col-auto">
                          <label for="orderReference" class="col-form-label">Referentie</label>
                          <EditOrderProp :locked="isOrderLocked"
                            id="orderReference"
                            :type="'text'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 12em;'"
                            :index="1"
                            :orderProperty="{value: reference}"
                            @changed="referenceChanged"
                          />
                        </div>
                        <div v-if="isPdfParsingOrder && order?.output?.orderDate" class="col-auto">
                          <label class="col-form-label">Orderdatum</label>
                          <EditOrderProp :locked="isOrderLocked"
                            id="orderDate"
                            :type="'date'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 10em;'"
                            :index="1"
                            :orderProperty="{value: formatDateForInput(order.output.orderDate)}"
                            @changed="orderDateChanged"
                          />
                        </div>
                        <div v-else-if="order?.output?.orderDate" class="col-auto d-flex align-items-end">
                          <div class="text-body-secondary mb-2">
                            <b>Orderdatum: </b> {{ formatDateForDisplay(order.output.orderDate) }}
                          </div>
                        </div>
                        <div class="col-auto d-flex align-items-end">
                          <div class="text-body-secondary mb-2">
                            <b>Totaalprijs: </b> {{ formatCurrency(order?.output?.netAmountCustomer, true) }}
                          </div>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-auto">
                          <label for="debtorCode" class="col-form-label">Debiteurnummer</label>
                          <div class="d-flex align-items-center gap-2">
                            <EditOrderProp :locked="isOrderLocked"
                              id="debtorCode"
                              :type="'text'"
                              :classStr="'text-begin'"
                              :styleStr="'max-width: 8em;'"
                              :index="1"
                              :orderProperty="{value: debtorCode}"
                              @changed="debtorCodeChanged"
                            />
                            <button
                              v-if="isOrderEditable && debtorCode"
                              type="button"
                              class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                              :disabled="loadingUnit4Addresses"
                              @click="fetchUnit4Addresses"
                              title="Haal factuuradrescode en alle afleveradressen opnieuw op uit Unit4"
                            >
                              <span v-if="loadingUnit4Addresses" class="spinner-border spinner-border-sm" role="status"></span>
                              <i v-else class="bi bi-arrow-repeat"></i>
                              <span>{{ loadingUnit4Addresses ? 'Ophalen...' : 'Adressen ophalen' }}</span>
                            </button>
                          </div>
                        </div>
                        <div class="col-auto">
                          <label for="invoiceAddressCode" class="col-form-label">Factuuradrescode</label>
                          <EditOrderProp :locked="isOrderLocked"
                            id="invoiceAddressCode"
                            :type="'text'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 8em;'"
                            :index="1"
                            :orderProperty="{value: invoiceAddressCode}"
                            @changed="invoiceAddressCodeChanged"
                          />
                        </div>
                        <div class="col-auto">
                          <label for="orderTypeCode" class="col-form-label">Ordertypecode</label>
                          <EditOrderProp :locked="isOrderLocked"
                            id="orderTypeCode"
                            :type="'text'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 6em;'"
                            :index="1"
                            :orderProperty="{value: orderTypeCode}"
                            @changed="orderTypeCodeChanged"
                          />
                        </div>
                        <div class="col-auto">
                          <label for="contactEmail" class="col-form-label" title="Contactpersoon-e-mailadres (komt uit de AI-parser, kan hier aangevuld of gewijzigd worden).">Contact e-mail</label>
                          <EditOrderProp :locked="isOrderLocked"
                            id="contactEmail"
                            :type="'text'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 16em;'"
                            :index="1"
                            :orderProperty="{value: contactEmail}"
                            @changed="contactEmailChanged"
                          />
                        </div>
                        <div v-if="order?.output?.invoiceAddress?.inputAddress" class="col-auto">
                          <label class="col-form-label">Factuuradres klant</label>
                          <div v-html="order.output.invoiceAddress.inputAddress" class="border border-1 rounded-3 input-address-display-full">
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
              <div class="orderlines-list">
                <div v-for="(orderline, index) in orderlines" :key="index" class="orderline-item">
                  <div class="card mb-2">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <h5 class="card-title mb-0">Orderregel {{ orderline.orderLineNumber }}</h5>
                        <button
                          v-if="isOrderEditable && isFailedOrder && (isAdmin || canManageOrderLines)"
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteOrderLine(index)"
                          title="Verwijder orderregel"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                      <h6 class="card-subtitle mb-2 text-body-secondary">
                        <b>Prijs ex.btw:</b> {{ formatCurrency(orderline.linePrice, true) }}
                      </h6>
                      <div class="row custom-row ms-0 me-0">
                        <div class="col-auto">
                          <label for="aantal" class="col-form-label">Aantal</label>
                          <EditOrderlineProp :locked="isOrderLocked"
                            id="aantal"
                            :type="'number'"
                            :classStr="'text-begin quantity-input'"
                            :styleStr="'max-width: 8em;'"
                            :rowIndex="index"
                            :orderlineProperty="{ value: orderline.qtyOrdered }"
                            :round="0"
                            @changed="quantityChanged"
                          />
                        </div>
                        <div class="col-auto">
                          <div class="d-flex flex-column">
                            <label for="productCode" class="col-form-label">Productcode</label>
                            <div class="d-flex align-items-center">
                              <EditOrderlineProp :locked="isOrderLocked"
                                id="productCode"
                                :type="'text'"
                                :classStr="'text-begin'"
                                :styleStr="'max-width: 12em;'"
                                :rowIndex="index"
                                :orderlineProperty="{ 
                                  value: orderline.productIdentifier?.productCode,
                                  candidates: orderline.productIdentifier?.productCandidates
                                }"
                                dropdownItemsKey="candidates"
                                valueKey="productCode"
                                labelKey="description"
                                @changed="productCodeChanged"
                              />
                              <!-- Vector DB lookup button - alleen tonen voor admins met productDescription -->
                              <button
                                v-if="isOrderEditable && isAdmin && orderline.productDescription"
                                type="button"
                                class="btn btn-sm btn-secondary ms-2"
                                @click="handleProductDescriptionLookup(index)"
                                :title="`Zoek product met omschrijving: ${orderline.productDescription}`"
                              >
                                <i class="bi bi-search"></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Voorraad per warehouse (RTD=01 + ELS=04) via Unit4 mobilesales RequestProductPhysicalStock.
                             Frontend toont magazijn-aliassen (RTD/ELS); de backend en Unit4 requests gebruiken de numerieke codes.
                             Zichtbaar voor alle ingelogde gebruikers. -->
                        <div v-if="orderline.productIdentifier?.productCode" class="col-auto">
                          <label class="col-form-label">Voorraad (RTD / ELS)</label>
                          <div class="d-flex align-items-center stock-cell">
                            <span class="badge stock-badge me-1" :title="`Magazijn RTD (01) — vrij beschikbaar (peildatum ${stockDate || 'vandaag'})`">
                              RTD: {{ getStockDisplay(orderline.productIdentifier.productCode, '01') }}
                            </span>
                            <span class="badge stock-badge me-2" :title="`Magazijn ELS (04) — vrij beschikbaar (peildatum ${stockDate || 'vandaag'})`">
                              ELS: {{ getStockDisplay(orderline.productIdentifier.productCode, '04') }}
                            </span>
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-secondary"
                              @click="refreshLineStock(index)"
                              :disabled="isLineStockLoading(orderline.productIdentifier.productCode)"
                              title="Voorraad opnieuw ophalen"
                            >
                              <i class="bi" :class="isLineStockLoading(orderline.productIdentifier.productCode) ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'"></i>
                            </button>
                          </div>
                        </div>

                        <!-- Toon supplierPartId en customerPartId als productCode leeg is -->

                        <div class="col-auto">
                          <div class="d-flex flex-column">
                            <!-- Altijd een vaste hoogte behouden voor de label ruimte -->
                            <div>
                              <!-- Alleen label tonen als inputveld zichtbaar is -->
                              <label v-if="showCustomerPartId[index] || orderline.productIdentifier?.customerPartId" 
                                for="customerPartId" 
                                class="col-form-label">Debiteurproduct</label>
                              <!-- Onzichtbare placeholder label om ruimte te behouden als er geen label is -->
                              <div v-else class="invisible-label"></div>
                            </div>
                            
                            <!-- Input en button container -->
                            <div class="d-flex align-items-center">
                              <!-- Het veld is altijd aanwezig, maar de wrapper krijgt een width van 0 als het verborgen moet zijn -->
                              <div class="edit-field-wrapper" 
                                   :class="{'field-expanded': showCustomerPartId[index] || orderline.productIdentifier?.customerPartId}" 
                                   :style="!(showCustomerPartId[index] || orderline.productIdentifier?.customerPartId) ? 'width: 0px; padding: 0; margin: 0' : ''">
                                <!-- Bij vue moet v-if wel blijven om te zorgen dat het component echt verwijderd wordt als het niet nodig is -->
                                <EditOrderlineProp :locked="isOrderLocked"
                                  v-if="showCustomerPartId[index] || orderline.productIdentifier?.customerPartId"
                                  id="customerPartId"
                                  :type="'text'"
                                  :classStr="'text-begin'"
                                  :styleStr="'width: 100%;'"
                                  :rowIndex="index"
                                  :orderlineProperty="{ value: orderline.productIdentifier?.customerPartId }"
                                  @changed="customerPartIdChanged"
                                />
                              </div>
                              <!-- Zoekbutton staat los van de editfield zodat hij altijd op dezelfde hoogte blijft -->
                              <button
                                v-if="isOrderEditable"
                                type="button"
                                class="btn btn-sm btn-secondary ms-2"
                                @click="handleCustomerPartIdSearch(index)"
                                :title="getCustomerPartIdButtonTitle(index)"
                              >
                                <i class="bi bi-search"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div class="col-auto">
                          <label for="unitCode" class="col-form-label">Eenheid</label>
                          <EditOrderlineProp :locked="isOrderLocked"
                            id="unitCode"
                            :type="'text'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 5em;'"
                            :rowIndex="index"
                            :orderlineProperty="{ value: orderline.unitCode }"
                            @changed="unitCodeChanged"
                          />
                        </div>
                        
                        <div v-if="orderline.deliveryDateCustomer" class="col-auto">
                          <label class="col-form-label">Afleverdatum</label>
                          <EditOrderlineProp :locked="isOrderLocked"
                            id="deliveryDateCustomer"
                            :type="'date'"
                            :classStr="'text-begin'"
                            :styleStr="'max-width: 10em;'"
                            :rowIndex="index"
                            :orderlineProperty="{ value: formatDateForInput(orderline.deliveryDateCustomer) }"
                            @changed="deliveryDateChanged"
                          />
                        </div>
                        <div class="col-auto">
                          <label for="deliveryAddressCode" class="col-form-label">Afleveradrescode</label>
                          <div class="d-flex align-items-center gap-2">
                            <DeliveryAddressSelect
                              :locked="isOrderLocked"
                              :rowIndex="index"
                              :value="orderline.deliveryAddress?.id"
                              :candidates="orderline.deliveryAddress?.candidates || []"
                              :inputAddress="orderline.deliveryAddress?.inputAddress || ''"
                              @changed="addressCodeChanged"
                            />
                            <button
                              v-if="isOrderEditable && index < orderlines.length - 1 && orderline.deliveryAddress?.id"
                              type="button"
                              class="btn btn-sm btn-outline-secondary"
                              @click="copyAddressToSubsequentLines(index)"
                              title="Kopieer afleveradres naar alle volgende orderregels"
                            >
                              <i class="bi bi-arrow-down"></i>
                            </button>
                          </div>
                        </div>

                        <div v-if="orderline.deliveryAddress?.inputAddress" class="col-auto">
                            <label for="inputAddress" class="col-form-label">Afleveradres klant</label>
                            <div id="inputAddress" v-html="orderline.deliveryAddress.inputAddress" class="border border-1 rounded-3 input-address-display-full">
                            </div>
                        </div>

                        <div v-if="orderline.deliveryAddress?.selectedAddress" class="col-auto">
                            <label for="selectedAddress" class="col-form-label">Geselecteerd afleveradres</label>
                            <div id="selectedAddress" v-html="orderline.deliveryAddress.selectedAddress" class="border border-1 rounded-3 input-address-display-full">
                            </div>
                        </div>

                      </div>
                      <!-- Product omschrijving uit input data (admin only) -->
                      <div v-if="isAdmin && orderline.productDescription" class="mt-2">
                        <small class="text-muted product-description-text">
                          <i class="bi bi-file-text me-1"></i>{{ orderline.productDescription }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="isOrderEditable && isFailedOrder && (isAdmin || canManageOrderLines)"
                class="d-flex justify-content-start mt-2"
              >
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="isAutoSaving"
                  @click="addOrderLine()"
                  :title="isAutoSaving ? 'Wacht tot wijzigingen opgeslagen zijn' : 'Voeg een nieuwe orderregel toe'"
                >
                  <i class="bi bi-plus-lg me-1"></i> Orderregel toevoegen
                </button>
              </div>
            </div>
            <div v-if="selectedView == 'object' && !order.output" class="alert alert-info">
              Geen object data beschikbaar voor deze order.
            </div>
            <div v-if="selectedView == 'json'" :key="'json-div-' + order.id">
              <div class="d-flex justify-content-end align-items-center mb-2">
                <div class="btn-group me-2" role="group" aria-label="JSON view switch">
                  <button type="button" class="btn btn-sm" 
                    :class="jsonViewMode === 'tree' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="jsonViewMode = 'tree'">Tree</button>
                  <button type="button" class="btn btn-sm" 
                    :class="jsonViewMode === 'pretty' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="jsonViewMode = 'pretty'">Pretty</button>
                </div>
                <button v-if="isFailedOrder"
                  type="button" @click="retryOrderWithJson()" class="btn btn-primary me-2">
                  <i class="bi bi-arrow-repeat me-1"></i>
                  Handmatig versturen (JSON)
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary py-1 px-2" 
                  @click="copyJsonToClipboard()">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
              <json-tree 
                v-if="jsonViewMode === 'tree' && order.output" 
                :data="order.output"
                :key="'json-tree-' + order.id"
              ></json-tree>
              <editable-json-view
                v-else
                ref="editableJsonView"
                :editable="order.status?.id != 1 && order.status?.id != 3 && order.status?.id != 6"
                :initialJson="order.output"
                @updateJson="updateJson"
                @showToast="showToast"
                :key="'json-view-' + order.id"
              ></editable-json-view>
            </div> 
            <div v-if="selectedView == 'input'" class="position-relative" :key="'input-div-' + order.id">
              <div class="d-flex justify-content-end mb-2">
                <button v-if="isFailedOrder && inputType === 'xml'"
                  type="button" @click="retryOrderWithOriginalXml()" class="btn btn-primary me-2">
                  <i class="bi bi-arrow-repeat me-1"></i>
                  Handmatig versturen (Originele XML)
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary py-1 px-2" 
                  @click="copyToClipboard(cleanFormattedInput)">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
              <pre class="input-pre">
                <code :class="inputType">{{ cleanFormattedInput }}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>

    <!-- PDF Viewer Panel - slides in from right -->
    <transition name="slide-panel">
      <div v-if="hasPdfFile && showPdfViewer" class="pdf-panel">
        <!-- Resize Handle -->
        <div
          class="pdf-resize-handle"
          @mousedown="startPdfResize"
          title="Sleep om breedte aan te passen"
        >
          <div class="resize-indicator"></div>
        </div>
        
        <!-- PDF Content -->
        <div class="pdf-panel-content">
          <div class="pdf-panel-header">
            <span class="fw-bold">
              <i class="bi bi-file-pdf text-danger me-2"></i>
              Originele PDF
            </span>
            <div class="d-flex gap-1">
              <a 
                v-if="pdfBlobUrl"
                :href="pdfBlobUrl" 
                target="_blank" 
                class="btn btn-sm btn-outline-primary"
                title="Open in nieuw tabblad"
              >
                <i class="bi bi-box-arrow-up-right"></i>
              </a>
              <a 
                v-if="pdfBlobUrl"
                :href="pdfBlobUrl" 
                :download="'order-' + order.id + '.pdf'" 
                class="btn btn-sm btn-outline-secondary"
                title="Download PDF"
              >
                <i class="bi bi-download"></i>
              </a>
              <button 
                type="button" 
                class="btn btn-sm btn-outline-secondary"
                @click="showPdfViewer = false"
                title="Sluiten"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          <div class="pdf-iframe-container">
            <!-- Overlay to block iframe events during resize -->
            <div v-if="isResizingPdf" class="pdf-resize-overlay"></div>
            <!-- Loading state -->
            <div v-if="pdfLoading" class="d-flex justify-content-center align-items-center h-100">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">PDF laden...</span>
              </div>
            </div>
            <!-- Error state -->
            <div v-else-if="pdfError" class="d-flex justify-content-center align-items-center h-100 text-danger">
              <div class="text-center">
                <i class="bi bi-exclamation-triangle fs-1 mb-2"></i>
                <p>{{ pdfError }}</p>
                <button class="btn btn-sm btn-outline-primary" @click="loadPdf()">
                  <i class="bi bi-arrow-clockwise me-1"></i>Opnieuw proberen
                </button>
              </div>
            </div>
            <!-- PDF iframe -->
            <iframe 
              v-else-if="pdfBlobUrl"
              :src="pdfBlobUrl"
              class="pdf-iframe"
              title="Order PDF"
            ></iframe>
          </div>
        </div>
      </div>
    </transition>
  </div>
  <div v-else>
    <p>Loading...</p>
  </div>

  <!-- Duplicate Order Warning Modal -->
  <div class="modal fade" id="duplicateOrderModal" tabindex="-1" aria-labelledby="duplicateOrderModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="duplicateOrderModalLabel">⚠️ Order bestaat al in Unit4</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>Deze order bestaat al in Unit4, weet je zeker dat je deze order dubbel wilt maken?</p>
          <div v-if="duplicateInfo" class="mt-3">
            <strong>Order details:</strong>
            <ul class="mt-2">
              <li><strong>Reference:</strong> {{ duplicateInfo.reference }}</li>
              <li><strong>Debiteur:</strong> {{ duplicateInfo.debtorCode }}</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuleren</button>
          <button type="button" class="btn btn-warning" @click="proceedWithRetry">Ja, dubbel maken</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceFactory from "../services/ServiceFactory";
import api from "../services/axiosConfig";
import Utils from "../utils/Utils";
import EditOrderProp from "../components/EditOrderProp.vue";
import EditOrderlineProp from "../components/EditOrderlineProp.vue";
import DeliveryAddressSelect from "../components/DeliveryAddressSelect.vue";
import JsonTree from "../components/JsonTree.vue";
import OrderDetailTab from "../components/OrderDetailTab.vue";
import EditableJsonView from "../components/EditableJsonView.vue";
import { XMLParser } from 'fast-xml-parser';
import OAuth2AzureService from "@/services/OAuth2AzureService";
import { OrderStatusType } from "../types/OrderStatusType";
import toastService from "@/services/ToastService";
import OrderHistoryStore from "@/services/OrderHistoryStore";

export default {
  name: "OrderDetailPage",
  props: ["id", "isAdmin", "canManageOrderLines", "user"],
  emits: ["showToast"],
  components: {
    EditOrderlineProp,
    DeliveryAddressSelect,
    EditOrderProp,
    JsonTree,
    OrderDetailTab,
    EditableJsonView,
  },
  data() {
    return {
      orderService: {},
      order: null,
      orderlines: [],
      originalOutputLines: null,
      selectedView: "object",
      jsonViewMode: 'tree',
      orderstatussen: [],
      originalStatusId: null,
      invoiceAddressCode: null,
      debtorCode: null,
      orderTypeCode: null,
      contactEmail: "",
      formattedInput: '',
      inputType: '',
      service: null,
      reference: "",
      loading: false,
      error: null,
      fetchingProductCode: false,
      productCodeFetched: false,
      showSidebarRight: true,
      showPdfViewer: true,
      pdfPanelWidth: 500,
      isResizingPdf: false,
      pdfBlobUrl: null,
      pdfLoading: false,
      pdfError: null,
      selectedTab: 'printout',
      validationErrorsById: {}, // Bevat validatiefouten geïndexeerd op id
      currentProgress: 0,
      maxProgress: 100,
      showCustomerPartId: [], // Array om bij te houden welke debiteurproduct velden getoond moeten worden
      modalWarnings: {
        show: false,
        title: '',
        message: '',
        okButton: 'OK',
        cancelButton: 'Annuleren',
        type: 'warning', // Mogelijke waarden: 'success', 'error', 'warning', etc.
        width: '600px',
        onOk: () => {}, // Functie die wordt uitgevoerd bij het klikken op OK
        onCancel: () => {} // Functie die wordt uitgevoerd bij het klikken op Annuleren
      },
      addressCodeChangedState: false,
      loadingUnit4Addresses: false,
      unit4OrganisationName: null,
      jsonChanged: false,
      xmlChanged: false,
      duplicateInfo: null,
      pendingRetryOperation: null, // 'json', 'xml' or null
      duplicateModal: null,
      isAutoSaving: false,
      autoSaveTimer: null,
      historyVersion: 0,
      manuallyUnlocked: false,
      // Real-time fysieke voorraad per warehouse uit Unit4 mobilesales. Niet gepersisteerd —
      // wordt bij elke pageload opnieuw opgehaald, en per regel ververst via refreshLineStock().
      // Vorm: { "<productCode>": { "01": number|null, "04": number|null } }
      stockByProductCode: {},
      // Loading-flags per productCode zodat de refresh-icon kan spinnen op alleen die regel.
      stockLoadingByProductCode: {},
      stockDate: null,
      // Live Unit4-orderstatus voor de toggle-knop. 'O' = Onderhanden, 'B' = Wordt ingegeven,
      // null = nog niet geladen of order niet in Unit4. Wordt bij elke pageload opgehaald.
      unit4OrderStatus: null,
      unit4OrderStatusDescription: '',
      isLoadingUnit4Status: false,
      // True terwijl een Unit4-statuswijziging (toggle) loopt.
      isSettingUnit4Status: false,
    };
  },
  computed: {
    isStatusLocked() {
      return false //this.order.status.id === 1 || this.order.status.id === 3 || this.order.status.id === 6;
    },
    sortedHistory() {
      if (!this.order?.history) {
        return [];
      }
      return [...this.order.history].sort((a, b) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    },
    cleanFormattedInput() {
      const lines = this.formattedInput.split('\n');
      
      //'Verwijder lege regels aan het begin'
      while (lines.length > 0 && lines[0].trim() === '') {
        lines.shift();
      }
      
      // Verwijder lege regels aan het einde van de input string
      while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
        lines.pop();
      }
      
      return lines.join('\n');
    },
    hasDeletedOrderLines() {
      if (!this.originalOutputLines) return false;
      return this.orderlines.length < this.originalOutputLines.length;
    },
    isDuplicateOrder() {
      // Check of de order status "Dubbel orderaanbod" is
      return this.order?.status?.id === OrderStatusType.DUPLICATE_ORDER_OFFER;
    },
    isFailedOrder() {
      return this.order?.status?.id === OrderStatusType.FAILED ||
             this.order?.status?.id === OrderStatusType.FAILED_OTHER ||
             this.order?.status?.id === OrderStatusType.PROCESS_MANUALLY;
    },
    isPdfParsingOrder() {
      // Check if this order came from PDF parsing entrance point
      return this.order?.customer?.username === 'pdfparsing';
    },
    // True wanneer de RTD-voorraad (Rotterdam, warehouse 01) voor elke orderregel voldoende is
    // om de bestelde hoeveelheid te dekken. ELS (04) wordt bewust niet meegerekend. Zelfde product
    // op meerdere regels wordt geaggregeerd, want één voorraadpositie kan niet twee regels dubbel
    // dekken. Is de voorraad voor enig product onbekend (null) of te laag, dan retourneert dit false.
    rtdStockSufficient() {
      const lines = this.orderlines || [];
      if (lines.length === 0) return false;

      const requiredByCode = {};
      for (const line of lines) {
        const code = line?.productIdentifier?.productCode;
        if (!code) return false; // regel zonder productcode kunnen we niet tegen voorraad checken
        const qty = Number(line?.qtyOrdered);
        if (!Number.isFinite(qty)) return false;
        requiredByCode[code] = (requiredByCode[code] || 0) + qty;
      }

      for (const code of Object.keys(requiredByCode)) {
        const entry = this.stockByProductCode[code];
        const rtd = entry ? entry['01'] : null;
        if (rtd === null || rtd === undefined) return false; // voorraad onbekend → niet vrijgeven
        if (Number(rtd) < requiredByCode[code]) return false;
      }
      return true;
    },
    // True wanneer de order in Unit4 op status O (Onderhanden) staat.
    isOnderhanden() {
      return this.unit4OrderStatus === 'O';
    },
    // De toggle wisselt alleen tussen 'B' (Wordt ingegeven) en 'O' (Onderhanden). Bij andere
    // statussen (bv. 'Z' Afgehandeld) of een nog onbekende status mag er niet getoggeld worden.
    canToggleUnit4Status() {
      return this.unit4OrderStatus === 'B' || this.unit4OrderStatus === 'O';
    },
    onderhandenButtonLabel() {
      return this.isOnderhanden ? 'Terugzetten' : 'Op onderhanden';
    },
    onderhandenButtonDisabled() {
      if (this.isSettingUnit4Status || this.isLoadingUnit4Status) return true;
      if (!this.canToggleUnit4Status) return true;     // andere status of nog onbekend
      if (this.isOnderhanden) return false;            // terugzetten (O→B) mag altijd
      return !this.rtdStockSufficient;                 // op onderhanden (B→O): voorraad-eis
    },
    onderhandenButtonTitle() {
      if (this.isSettingUnit4Status) return 'Bezig met statuswijziging in Unit4...';
      if (this.isLoadingUnit4Status) return 'Unit4-status wordt opgehaald...';
      if (!this.canToggleUnit4Status) {
        const desc = this.unit4OrderStatusDescription || this.unit4OrderStatus;
        return desc
          ? `Order heeft Unit4-status "${desc}" — wisselen kan alleen tussen Wordt ingegeven en Onderhanden`
          : 'Unit4-status onbekend';
      }
      if (this.isOnderhanden) return 'Zet de order terug naar Wordt ingegeven (Unit4 status B)';
      if (this.rtdStockSufficient) return 'Zet de order op Onderhanden (Unit4 status O)';
      return 'Onvoldoende RTD-voorraad (Rotterdam) voor de bestelde hoeveelheid';
    },
    isOrderLocked() {
      if (!this.order) return false;
      if (this.manuallyUnlocked) return false;
      // Een order mag alleen gelockt zijn als hij daadwerkelijk in Unit4 is aangemaakt.
      // vo_nummer alleen is niet voldoende: bij een gefaalde retry kan het door de
      // fetchSalesOrder-fallback in updateOrderRequestToEportal alsnog gevuld raken.
      // Daarom uitsluiten op basis van failure-statussen.
      if (!this.order.vo_nummer) return false;
      return !this.isFailedOrder;
    },
    isOrderEditable() {
      return !!this.order && !this.isOrderLocked;
    },
    canUndo() {
      // historyVersion is reactive trigger; read it so Vue tracks dependency
      return this.historyVersion >= 0 && OrderHistoryStore.canUndo(this.order?.id);
    },
    canRedo() {
      return this.historyVersion >= 0 && OrderHistoryStore.canRedo(this.order?.id);
    },
    hasPdfFile() {
      // Check if this order has an associated PDF file
      return !!this.order?.pdf_filename;
    },
    pdfApiUrl() {
      if (!this.order?.id) return null;
      return `/api/orders/${this.order.id}/pdf`;
    }
  },
  methods: {
    // PDF loading method
    async loadPdf() {
      if (!this.order?.id || !this.hasPdfFile) return;
      
      // Cleanup old blob URL
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
      
      this.pdfLoading = true;
      this.pdfError = null;
      
      try {
        const response = await api.get(this.pdfApiUrl, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.pdfBlobUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.pdfError = 'Kon PDF niet laden';
      } finally {
        this.pdfLoading = false;
      }
    },
    
    // PDF Panel resize methods
    startPdfResize(e) {
      this.isResizingPdf = true;
      e.preventDefault();
      document.addEventListener('mousemove', this.handlePdfResize);
      document.addEventListener('mouseup', this.stopPdfResize);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },
    handlePdfResize(e) {
      if (!this.isResizingPdf) return;
      const newWidth = window.innerWidth - e.clientX;
      // Minimum 350px, maximum 70% of viewport
      this.pdfPanelWidth = Math.min(Math.max(350, newWidth), window.innerWidth * 0.7);
    },
    stopPdfResize() {
      if (this.isResizingPdf) {
        this.isResizingPdf = false;
        document.removeEventListener('mousemove', this.handlePdfResize);
        document.removeEventListener('mouseup', this.stopPdfResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Save preference
        localStorage.setItem('orderPdfPanelWidth', this.pdfPanelWidth.toString());
      }
    },
    getColor(status) {
      return Utils.getColorForOrderStatus(status);
    },
    getPrice(price) {
      return Utils.formatCurrency(price);
    },
    async loadOrderDetail() {
      try {
        this.orderService = ServiceFactory.getService("OrderService");
        this.order = await this.orderService.getOrder(this.id);

        if (!this.order || !this.order.id) {
          this.$router.push('/orders');
          return;
        }

        // Initialise undo/redo store; only restore local snapshots for editable orders
        OrderHistoryStore.init(this.user?.token);
        if (!this.isOrderLocked) {
          const { snapshot, serverChanged } = OrderHistoryStore.load(this.order.id, this.order.output);
          if (serverChanged) {
            this.$emit('showToast', ['Order is op server bijgewerkt — lokale concept-wijzigingen verworpen', 'bg-warning']);
          } else if (snapshot) {
            this.order.output = snapshot;
          }
          this.historyVersion++;
        }

        this.orderlines = this.order?.output?.lines || [];
        this.originalOutputLines = JSON.parse(JSON.stringify(this.orderlines));
        this.orderstatussen = await this.orderService.getOrderstatussen();
        this.originalStatusId = this.order?.status?.id;
        this.invoiceAddressCode = this.order?.output?.invoiceAddressCode;
        this.debtorCode = this.order?.output?.debtorCode;
        this.orderTypeCode = this.order?.output?.orderTypeCode || "EDI";
        this.contactEmail = this.order?.output?.contactEmail || "";
        this.reference = this.order?.output?.reference || this.order?.reference || "";
        this.unit4OrganisationName = this.order?.customer?.organisation_name || null;
        this.formatInput();
        // Voorraad real-time ophalen (warehouses 01 + 04) — silent: faalt het, dan tonen we '-'
        this.loadStock();
        // Live Unit4-orderstatus ophalen voor de Onderhanden/Wordt-ingegeven toggle-knop.
        this.loadUnit4Status();
      } catch (error) {
        console.error('Error loading order details:', error);
        this.$router.push('/orders');
      }
    },
    // Haalt de voorraad voor alle (of een subset van) orderline-productcodes op via de backend.
    // Optionele `productCodes` filter wordt gebruikt door de per-regel refresh-knop zodat alleen
    // die ene regel een Unit4 call triggert in plaats van de hele lijst.
    async loadStock(productCodes = null) {
      if (!this.order?.id) return;
      const codesToLoad = Array.isArray(productCodes) && productCodes.length > 0
        ? productCodes
        : Array.from(new Set((this.orderlines || [])
            .map(l => l?.productIdentifier?.productCode)
            .filter(Boolean)));

      if (codesToLoad.length === 0) return;

      // Markeer alle te-laden codes als loading zodat hun refresh-icon spint.
      for (const code of codesToLoad) {
        this.stockLoadingByProductCode = { ...this.stockLoadingByProductCode, [code]: true };
      }

      try {
        const res = await this.orderService.fetchOrderStock(this.order.id, productCodes);
        if (res?.success && res.stocks) {
          // Merge in plaats van overschrijven — anders verliest een per-regel refresh
          // de cijfers van andere regels.
          this.stockByProductCode = { ...this.stockByProductCode, ...res.stocks };
          if (res.stockDate) this.stockDate = res.stockDate;
        }
      } catch (err) {
        console.error('Voorraad ophalen mislukt:', err);
      } finally {
        for (const code of codesToLoad) {
          const next = { ...this.stockLoadingByProductCode };
          delete next[code];
          this.stockLoadingByProductCode = next;
        }
      }
    },
    refreshLineStock(index) {
      const code = this.orderlines?.[index]?.productIdentifier?.productCode;
      if (!code) return;
      this.loadStock([code]);
    },
    // Haalt de live Unit4-orderstatus op (admin-only) zodat de toggle-knop weet of de order op
    // Onderhanden (O) of Wordt ingegeven (B) staat. Silent: faalt het, dan blijft de status null
    // en is de knop disabled.
    async loadUnit4Status() {
      if (!this.isAdmin) return;
      if (!this.order?.id || !this.order?.vo_nummer) {
        this.unit4OrderStatus = null;
        this.unit4OrderStatusDescription = '';
        return;
      }
      this.isLoadingUnit4Status = true;
      try {
        const res = await this.orderService.getUnit4OrderStatus(this.order.id);
        if (res?.success) {
          this.unit4OrderStatus = res.orderStatus ?? null;
          this.unit4OrderStatusDescription = res.orderStatusDescription || '';
        }
      } catch (err) {
        console.error('Unit4-status ophalen mislukt:', err);
      } finally {
        this.isLoadingUnit4Status = false;
      }
    },
    // Toggle de Unit4-orderstatus: O→B (terugzetten naar Wordt ingegeven) of B→O (op Onderhanden).
    // De enable-voorwaarden (voldoende RTD-voorraad voor B→O, alleen wisselen tussen B en O) zitten
    // op de knop; hier voeren we de gekozen richting uit.
    async toggleUnit4Status() {
      if (!this.order?.vo_nummer || this.isSettingUnit4Status || !this.canToggleUnit4Status) return;
      const target = this.isOnderhanden ? 'B' : 'O';
      this.isSettingUnit4Status = true;
      try {
        const result = await this.orderService.setOrderUnit4Status(this.order.id, target);
        if (result.success) {
          const msg = target === 'O'
            ? 'Order op Onderhanden (status O) gezet in Unit4'
            : 'Order teruggezet naar Wordt ingegeven (status B) in Unit4';
          this.$emit('showToast', [msg, 'bg-success']);
          // Optimistisch bijwerken zodat de knop direct omklapt; loadOrderDetail ververst daarna
          // de history-log en haalt de definitieve Unit4-status opnieuw op.
          this.unit4OrderStatus = result.orderStatus || target;
          this.unit4OrderStatusDescription = result.orderStatusDescription || '';
          this.loadOrderDetail();
        } else {
          this.$emit('showToast', [`Statuswijziging mislukt: ${result.message || 'Onbekende fout'}`, 'bg-danger']);
        }
      } catch (error) {
        console.error('Fout bij Unit4-statuswijziging:', error);
        this.$emit('showToast', [error.message || 'Onverwachte fout bij statuswijziging', 'bg-danger']);
      } finally {
        this.isSettingUnit4Status = false;
      }
    },
    isLineStockLoading(productCode) {
      return !!this.stockLoadingByProductCode[productCode];
    },
    getStockDisplay(productCode, warehouse) {
      if (this.isLineStockLoading(productCode)) return '…';
      const entry = this.stockByProductCode[productCode];
      if (!entry) return '–';
      const value = entry[warehouse];
      if (value === null || value === undefined) return '–';
      return value;
    },
    commitChange() {
      if (!this.isOrderEditable || !this.order?.id) return;
      OrderHistoryStore.commit(this.order.id, this.order.output);
      this.historyVersion++;
      this.scheduleAutoSave();
    },
    scheduleAutoSave() {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => this.autoSave(), 400);
    },
    async autoSave() {
      if (!this.isOrderEditable || !this.order?.id) return;
      this.isAutoSaving = true;
      try {
        const res = await this.orderService.saveOrderOutput(this.order.id, this.order.output);
        if (res.success) {
          OrderHistoryStore.markSaved(this.order.id, this.order.output);
          const fresh = res.data;
          if (fresh) {
            this.order.vo_nummer = fresh.vo_nummer;
            this.order.status = fresh.status;
            this.order.history = fresh.history;
            this.order.updated_at = fresh.updated_at;
          }
        } else if (res.status === 409) {
          this.$emit('showToast', ['Order is reeds in Unit4 — wijzigingen niet opgeslagen', 'bg-warning']);
          OrderHistoryStore.clear(this.order.id);
          await this.loadOrderDetail();
        } else if (res.status !== 401) {
          this.$emit('showToast', [`Auto-opslaan mislukt: ${res.message || 'onbekende fout'}`, 'bg-danger']);
        }
      } catch (error) {
        console.error('autoSave fout:', error);
        this.$emit('showToast', [`Auto-opslaan mislukt: ${error.message || 'onbekende fout'}`, 'bg-danger']);
      } finally {
        this.isAutoSaving = false;
      }
    },
    async flushPendingAutoSave() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
        await this.autoSave();
      }
    },
    undoChange() {
      if (!this.order?.id) return;
      const snap = OrderHistoryStore.undo(this.order.id);
      if (snap) {
        this.order.output = snap;
        this.orderlines = snap.lines || [];
        this.invoiceAddressCode = snap.invoiceAddressCode ?? this.invoiceAddressCode;
        this.debtorCode = snap.debtorCode ?? this.debtorCode;
        this.orderTypeCode = snap.orderTypeCode ?? this.orderTypeCode;
        this.contactEmail = snap.contactEmail ?? this.contactEmail;
        this.reference = snap.reference ?? this.reference;
        this.historyVersion++;
        this.scheduleAutoSave();
      }
    },
    redoChange() {
      if (!this.order?.id) return;
      const snap = OrderHistoryStore.redo(this.order.id);
      if (snap) {
        this.order.output = snap;
        this.orderlines = snap.lines || [];
        this.invoiceAddressCode = snap.invoiceAddressCode ?? this.invoiceAddressCode;
        this.debtorCode = snap.debtorCode ?? this.debtorCode;
        this.orderTypeCode = snap.orderTypeCode ?? this.orderTypeCode;
        this.contactEmail = snap.contactEmail ?? this.contactEmail;
        this.reference = snap.reference ?? this.reference;
        this.historyVersion++;
        this.scheduleAutoSave();
      }
    },
    getHeader(txt) {
      return txt && txt !== "" ? " - " + txt : "";
    },
    getFormattedDate(date) {
      const formattedDate = Utils.formatDateTimeString(date);
      if (!formattedDate) {
        return "#:#";
      }
      return formattedDate;
    },
    getFormatedDateTime(date) {
      const formattedDate = Utils.getCustomFormattedDate(date);
      if (!formattedDate) {
        return "#:#";
      }
      return formattedDate;
    },
    addressCodeChanged(value, index) {
      console.log(`addressCodeChanged: (${value}, ${index})`);
      this.orderlines[index].deliveryAddress.id = value;
      
      // Voeg null check toe om undefined error te voorkomen
      const foundCandidate = this.orderlines[index].deliveryAddress.candidates?.find(candidate => candidate.id === value);
      if (foundCandidate) {
        this.orderlines[index].deliveryAddress.selectedAddress = foundCandidate.address;
        // BELANGRIJK: Ook in order.output opslaan zodat het bewaard blijft bij retry
        if (this.order?.output?.lines?.[index]?.deliveryAddress) {
          this.order.output.lines[index].deliveryAddress.selectedAddress = foundCandidate.address;
        }
      }
      
      // Zorg dat order.output.lines ook bestaat voordat we de id zetten
      if (this.order?.output?.lines?.[index]?.deliveryAddress) {
        this.order.output.lines[index].deliveryAddress.id = value;
      }
      this.commitChange();
    },
    retryToastFromResult(orderData, defaultSuccessMessage) {
      const statusId = orderData?.status_id ?? orderData?.status?.id;
      // 6 = MANUALLY_PROCESSED, 10 = DIFFERENT_ORDER_AMOUNT (beide succesvol ingelopen)
      const isSuccess = statusId === 6 || statusId === 10;
      const isDifferent = statusId === 10;
      const statusLabel = orderData?.error_message || orderData?.status?.label || '';
      if (isSuccess) {
        const message = isDifferent
          ? `${defaultSuccessMessage} (let op: bedrag wijkt af van Unit4)`
          : defaultSuccessMessage;
        return { color: 'bg-success', message };
      }
      return { color: 'bg-danger', message: statusLabel || 'Order niet succesvol ingelopen' };
    },
    copyAddressToSubsequentLines(fromIndex) {
      const source = this.orderlines[fromIndex].deliveryAddress;
      if (!source?.id) return;
      for (let i = fromIndex + 1; i < this.orderlines.length; i++) {
        this.orderlines[i].deliveryAddress = {
          ...(this.orderlines[i].deliveryAddress || {}),
          id: source.id,
          selectedAddress: source.selectedAddress || '',
          candidates: source.candidates || this.orderlines[i].deliveryAddress?.candidates,
        };
        if (this.order?.output?.lines?.[i]?.deliveryAddress) {
          this.order.output.lines[i].deliveryAddress.id = source.id;
          this.order.output.lines[i].deliveryAddress.selectedAddress = source.selectedAddress || '';
        }
      }
      this.$emit('showToast', [`Afleveradres gekopieerd naar ${this.orderlines.length - fromIndex - 1} volgende regel(s)`, 'bg-success']);
      this.commitChange();
    },
    unitCodeChanged(newUnitCode, index) {
      this.orderlines[index].unitCode = newUnitCode;
      this.order.output.lines[index].unitCode = newUnitCode;
      this.commitChange();
    },
    productCodeChanged(value, index) {
      console.log(`productCodeChanged: (${value}, ${index})`);
      this.orderlines[index].productIdentifier.productCode = value;
      this.order.output.lines[index].productIdentifier.productCode = value;

      // BELANGRIJK: Ook de candidates bewaren in order.output zodat ze niet verloren gaan bij retry
      if (this.orderlines[index].productIdentifier.productCandidates) {
        this.order.output.lines[index].productIdentifier.productCandidates = this.orderlines[index].productIdentifier.productCandidates;
      }
      this.commitChange();
    },
    
    // Bepaal welke tooltip tekst voor de zoekbutton getoond moet worden
    getCustomerPartIdButtonTitle(index) {
      if (this.showCustomerPartId[index] || this.orderlines[index]?.productIdentifier?.customerPartId) {
        return "Haal productcode op met dit debiteurproduct";
      } else {
        return "Toon debiteurproduct invoerveld";
      }
    },
    
    // Handle klik op de zoekbutton - eerst veld tonen, daarna productcode ophalen
    handleCustomerPartIdSearch(index) {
      // Als het veld nog niet zichtbaar is, maak het zichtbaar
      if (!this.showCustomerPartId[index] && !this.orderlines[index]?.productIdentifier?.customerPartId) {
        // Initialiseer de array indien nodig
        if (!this.showCustomerPartId || !Array.isArray(this.showCustomerPartId)) {
          this.showCustomerPartId = new Array(this.orderlines.length).fill(false);
        }
        
        // Zet de flag om het veld te tonen (Vue 3 manier)
        // We maken een nieuwe kopie van de array om reactief updaten te garanderen
        const updatedArray = [...this.showCustomerPartId];
        updatedArray[index] = true;
        this.showCustomerPartId = updatedArray;
        
        // Focus het veld na het renderen
        this.$nextTick(() => {
          const inputElement = document.getElementById(`customerPartId-${index}`);
          if (inputElement) {
            inputElement.click(); // Triggert de click event die het veld activeert
          }
        });
      } else {
        // Als er een waarde is, haal productcode op
        this.fetchProductCode(index);
      }
    },
    customerPartIdChanged(value, index) {
      console.log(`customerPartIdChanged: (${value}, ${index})`);
      if (!this.orderlines[index].productIdentifier) {
        this.orderlines[index].productIdentifier = {};
      }
      this.orderlines[index].productIdentifier.customerPartId = value;
      this.order.output.lines[index].productIdentifier.customerPartId = value;
      this.commitChange();
    },
    quantityChanged(value, index) {
      console.log(`quantityChanged: (${value}, ${index})`);
      const quantity = parseFloat(value) || 0;
      this.orderlines[index].qtyOrdered = quantity;
      this.order.output.lines[index].qtyOrdered = quantity;

      // Update de totaalprijs van de orderline
      if (this.orderlines[index].unitPrice) {
        this.orderlines[index].linePrice = quantity * this.orderlines[index].unitPrice;
        this.order.output.lines[index].linePrice = this.orderlines[index].linePrice;
      }
      this.commitChange();
    },
    async fetchProductCode(index) {
      try {
        // Voeg een kleine vertraging toe om ervoor te zorgen dat de change event eerst wordt afgehandeld
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Zorg dat orderline en productIdentifier bestaan
        if (!this.orderlines[index]) {
          this.$emit('showToast', ["Orderregel niet gevonden", "bg-danger"]);
          return;
        }
        
        // Zorg ervoor dat de productIdentifier structuur bestaat
        if (!this.orderlines[index].productIdentifier) {
          this.orderlines[index].productIdentifier = {};
          
          if (this.order.output?.lines[index]) {
            this.order.output.lines[index].productIdentifier = {};
          }
        }
        
        const customerPartId = this.orderlines[index].productIdentifier.customerPartId;
        
        // Controleer of er een debiteurproduct is ingevuld
        if (!customerPartId) {
          this.$emit('showToast', ["Vul eerst een debiteurproduct in", "bg-warning"]);
          return;
        }
        
        const data = {
          orderId: this.order.id,
          orderLineNumber: this.orderlines[index].orderLineNumber,
          customerPartId: customerPartId,
          unitCode: this.orderlines[index].unitCode,
          debtorId: this.order.output?.debtorCode
        };
        
        // Toon loading indicatie
        this.$emit('showToast', ["Productcode ophalen...", "bg-info"]);
        
        // Vraag de productCode op met customerPartId
        const result = await this.orderService.fetchProductCode(data);
        
        if (result.success && result?.productCode) {
          // Update productCode in lokale data
          //this.orderlines[index].productIdentifier.productCode = result.productCode;
          this.orderlines[index].productIdentifier = {
            productCode: result.productCode,
            customerPartId: result.customerPartId
          };
          if(result?.unitCode){
            this.orderlines[index].unitCode = result.unitCode;
          }
          
          // Zorg ervoor dat ook de output structuur correct is 
          if (this.order.output?.lines?.[index]) {
            if (!this.order.output.lines[index].productIdentifier) {
              this.order.output.lines[index].productIdentifier = {};
            }
            this.order.output.lines[index].productIdentifier.productCode = result.productCode;
          }
          
          this.$emit('showToast', ["Productcode succesvol opgehaald", "bg-success"]);
          this.commitChange();
        } else {
          this.$emit('showToast', [
            result.message || "Geen productcode gevonden voor dit debiteurproduct",
            "bg-warning"
          ]);
        }
      } catch (error) {
        console.error("Fout bij ophalen productcode:", error);
        this.$emit('showToast', ["Er is een fout opgetreden bij het ophalen van de productcode", "bg-danger"]);
      }
    },
    async handleProductDescriptionLookup(index) {
      let toastId = null;
      try {
        const description = this.orderlines[index].productDescription;
        
        if (!description) {
          this.$emit('showToast', ["Geen productomschrijving beschikbaar", "bg-warning"]);
          return;
        }
        
        // Toon persistente loading toast met spinner en zoekstring
        const searchPreview = description.length > 60 ? description.substring(0, 60) + '...' : description;
        toastId = toastService.showLoadingToast(
          `⏱️ Zoeken naar: "${searchPreview}" (2-3 min)`, 
          "bg-info"
        );
        
        // Maak API call naar backend
        const response = await this.orderService.lookupProductByDescription(description);
        
        if (response.success && response.candidates && response.candidates.length > 0) {
          // Zorg dat productIdentifier bestaat in orderlines
          if (!this.orderlines[index].productIdentifier) {
            this.orderlines[index].productIdentifier = {};
          }
          
          // Zorg dat productIdentifier bestaat in order.output
          if (!this.order.output.lines[index].productIdentifier) {
            this.order.output.lines[index].productIdentifier = {};
          }
          
          // Voeg candidates toe aan de orderline voor de dropdown
          const mappedCandidates = response.candidates.map((candidate, idx) => ({
            number: idx + 1,
            productCode: candidate.product_code || candidate.productCode,
            description: candidate.description,
            similarity: candidate.similarity
          }));
          
          this.orderlines[index].productIdentifier.productCandidates = mappedCandidates;
          // BELANGRIJK: Ook in order.output opslaan zodat het bewaard blijft bij retry
          this.order.output.lines[index].productIdentifier.productCandidates = mappedCandidates;
          
          // Als er maar 1 candidate is, vul deze automatisch in
          if (mappedCandidates.length === 1) {
            const autoFilledProduct = mappedCandidates[0];
            this.orderlines[index].productIdentifier.productCode = autoFilledProduct.productCode;
            this.order.output.lines[index].productIdentifier.productCode = autoFilledProduct.productCode;
            
            // Update toast naar succes met auto-fill bericht
            toastService.hideLoadingToast(toastId, `✅ 1 product gevonden en automatisch ingevuld: ${autoFilledProduct.productCode}`, "bg-success", true);
          } else {
            // Meerdere candidates: gebruiker moet kiezen
            toastService.hideLoadingToast(toastId, `✅ ${response.candidates.length} producten gevonden - klik op het productcode veld om te kiezen`, "bg-success", true);
          }
          
          // Trigger re-render van EditOrderlineProp door een kleine timeout
          this.$forceUpdate();
          this.commitChange();
        } else {
          // Update toast naar waarschuwing
          toastService.hideLoadingToast(toastId, "Geen producten gevonden voor deze omschrijving", "bg-warning", true);
        }
      } catch (error) {
        console.error("Fout bij product lookup:", error);
        
        // Update toast naar error
        if (error.message && error.message.includes('timeout')) {
          toastService.hideLoadingToast(toastId, "⏱️ Timeout: De product lookup duurde te lang. Probeer het opnieuw.", "bg-danger", true);
        } else {
          toastService.hideLoadingToast(toastId, "Er is een fout opgetreden bij het zoeken van producten", "bg-danger", true);
        }
      }
    },
    select(view) {
      this.selectedView = view;
    },
    async updateJson(newJson) {
      this.jsonChanged = true;
      this.order.output = newJson;
      this.orderlines = this.order.output?.lines || [];
      this.commitChange();
    },
    showToast(message) {
      this.$emit("showToast", message);
    },
    async updateOrderStatus() {
      if (this.isStatusLocked) {
        this.$emit("showToast", ["Deze orderstatus kan niet worden gewijzigd", "bg-warning"]);
        this.order.status.id = this.originalStatusId;
        return;
      }
      try {
        const result = await this.orderService.updateOrderStatus(this.order.id, this.order.status.id);
        if (result.success) {
          this.$emit("showToast", ["Orderstatus succesvol bijgewerkt", "bg-success"]);
          this.originalStatusId = this.order.status.id;
        } else {
          this.$emit("showToast", [`Fout bij het bijwerken van de orderstatus: ${result.message}`, "bg-danger"]);
          this.order.status.id = this.originalStatusId;
        }
      } catch (error) {
        console.error("Fout bij het updaten van de orderstatus:", error);
        this.$emit("showToast", ["Er is een fout opgetreden bij het bijwerken van de orderstatus", "bg-danger"]);
        this.order.status.id = this.originalStatusId;
      }
    },
    async retryOrderWithJson() {
      if (!this.orderlines || this.orderlines.length === 0) {
        this.$emit('showToast', ['Een order moet minimaal één orderregel bevatten', 'bg-danger']);
        return;
      }

      // Eerst duplicate check uitvoeren
      const duplicateCheck = await this.checkForDuplicate();
      if (duplicateCheck.shouldProceed === false) {
        return; // Gebruiker heeft geannuleerd of er was een fout
      }
      
      if (duplicateCheck.isDuplicate) {
        // Toon waarschuwing en wacht op gebruiker beslissing
        this.duplicateInfo = duplicateCheck.info;
        this.pendingRetryOperation = 'json';
        this.showDuplicateWarning();
        return;
      }
      
      // Geen duplicate, ga direct verder
      await this.performRetryWithJson();
    },

    async performRetryWithJson() {
      try {
        let dataToSend;
        
        if (this.selectedView === "json") {
          // Gebruik de JSON data uit de JSON tab
          const editableJsonView = this.$refs.editableJsonView;
          if (editableJsonView) {
            const jsonContent = editableJsonView.jsonContent;
            dataToSend = JSON.parse(jsonContent);
          } else {
            dataToSend = this.order.output;
          }
        } else {
          // Object view - gebruik de huidige output data
          dataToSend = this.order.output;
        }

        console.log('🚀 Data being sent to retryOrder:', JSON.stringify(dataToSend, null, 2));
        console.log('🚀 Reference in dataToSend:', dataToSend.reference);

        // Stuur de aangepaste JSON data
        const result = await this.orderService.retryOrder(this.order.id, dataToSend);
        if (result.success) {
          const { color, message } = this.retryToastFromResult(result.data, "Order is opnieuw ingestuurd met aangepaste data");
          this.$emit('showToast', [message, color]);
          this.loadOrderDetail();
        } else {
          this.$emit('showToast', [`Er is een fout opgetreden bij het opnieuw versturen van de order: ${result.message || 'Onbekende fout'}`, 'bg-danger']);
        }
      } catch (error) {
        console.error("Fout bij opnieuw insturen order:", error);
        this.$emit('showToast', [error.message || "Er is een onverwachte fout opgetreden bij het opnieuw versturen van de order", 'bg-danger']);
      }
    },
    async checkForDuplicate() {
      try {
        // Pass current reference and debtorCode (may have been edited)
        const result = await this.orderService.checkDuplicate(
          this.order.id,
          this.order.output.reference,
          this.order.output.debtorCode
        );
        if (result.success) {
          return {
            isDuplicate: result.exists,
            info: result,
            shouldProceed: true
          };
        } else {
          return {
            isDuplicate: false,
            info: null,
            shouldProceed: true
          };
        }
      } catch (error) {
        console.error("Fout bij duplicate check:", error);
        return {
          isDuplicate: false,
          info: null,
          shouldProceed: true
        };
      }
    },

    async retryOrderWithOriginalXml() {
      // Eerst duplicate check uitvoeren
      const duplicateCheck = await this.checkForDuplicate();
      if (duplicateCheck.shouldProceed === false) {
        return; // Gebruiker heeft geannuleerd of er was een fout
      }
      
      if (duplicateCheck.isDuplicate) {
        // Toon waarschuwing en wacht op gebruiker beslissing
        this.duplicateInfo = duplicateCheck.info;
        this.pendingRetryOperation = 'xml';
        this.showDuplicateWarning();
        return;
      }
      
      // Geen duplicate, ga direct verder
      await this.performRetryWithOriginalXml();
    },

    async performRetryWithOriginalXml() {
      try {
        // Stuur de originele input (XML) opnieuw
        const xmlInput = this.cleanFormattedInput;
        const result = await this.orderService.retryOrderWithOriginalInput(this.order.id, xmlInput);
        if (result.success) {
          const { color, message } = this.retryToastFromResult(result.data, "Order is opnieuw ingestuurd met originele XML input");
          this.$emit('showToast', [message, color]);
          this.loadOrderDetail();
        } else {
          this.$emit('showToast', [`Er is een fout opgetreden bij het opnieuw versturen van de order: ${result.message || 'Onbekende fout'}`, 'bg-danger']);
        }
      } catch (error) {
        console.error("Fout bij opnieuw insturen order met originele XML:", error);
        this.$emit('showToast', [error.message || "Er is een onverwachte fout opgetreden bij het opnieuw versturen van de order", 'bg-danger']);
      }
    },
    async saveAsNew() {
      // Toon een bevestigingsdialoog
      if (confirm("Weet je zeker dat je deze order als nieuw wilt opslaan? Dit zal een nieuwe order aanmaken ondanks dat de opgegeven referentie niet uniek is.")) {
        // Toon een laadspinner
        this.showToast("Order wordt opgeslagen als nieuw...");
        const result = await this.orderService.saveAsNew(this.order.id, this.order.output);
        if (result.success) {
          this.$emit('showToast', [`Order ${result.data.vo_nummer} is succesvol opgeslagen als nieuw`, 'bg-success']);
          this.$router.push('/orders');
        } else {
          this.$emit('showToast', ['Er is een fout opgetreden bij het opslaan van de order als nieuw', 'bg-danger']);
        }
      }
    },
    invoiceAddressCodeChanged(invoiceAddressCode, index) {
      console.log(`Invoice Address Code gewijzigd: ${invoiceAddressCode} op index ${index}`);
      this.invoiceAddressCode = invoiceAddressCode;
      this.order.output.invoiceAddressCode = invoiceAddressCode;
      this.commitChange();
    },
    orderTypeCodeChanged(orderTypeCode, index) {
      console.log(`OrderTypeCode gewijzigd: ${orderTypeCode} op index ${index}`);
      this.orderTypeCode = orderTypeCode;
      this.order.output.orderTypeCode = orderTypeCode;
      this.commitChange();
    },
    contactEmailChanged(contactEmail) {
      const value = (contactEmail || "").trim();
      this.contactEmail = value;
      // Bewaren in order.output zodat het meegaat bij retry/opnieuw verwerken
      this.order.output = {
        ...this.order.output,
        contactEmail: value
      };
      this.commitChange();
    },
    referenceChanged(newReference, index) {
      console.log(`Referentie gewijzigd: ${newReference} op index ${index}`);
      console.log('Voor update - order.output.reference:', this.order.output.reference);

      // Update lokale data properties
      this.reference = newReference;

      // Force reactivity by creating new object
      this.order.output = {
        ...this.order.output,
        reference: newReference
      };

      this.order.reference = newReference;

      console.log('Na update - order.output.reference:', this.order.output.reference);
      console.log('Complete order.output:', JSON.stringify(this.order.output, null, 2));
      this.commitChange();
    },
    async debtorCodeChanged(debtorCode, index) {
      console.log(`Debiteurnummer gewijzigd: ${debtorCode} op index ${index}`);

      // Update lokale data (customerCode === debtorCode in Unit4)
      this.debtorCode = debtorCode;
      this.order.output.debtorCode = debtorCode;
      this.order.output.customerCode = debtorCode;
      this.commitChange();

      // Sla op naar server (legacy endpoint, blijft bestaan voor compat)
      try {
        const result = await this.orderService.updateDebtorCode(this.order.id, debtorCode);
        if (result.success === false) {
          this.$emit('showToast', [`Fout bij opslaan debiteurnummer: ${result.message}`, 'bg-danger']);
        } else {
          this.$emit('showToast', ['Debiteurnummer succesvol opgeslagen', 'bg-success']);
        }
      } catch (error) {
        console.error('Fout bij opslaan debiteurnummer:', error);
        this.$emit('showToast', ['Fout bij opslaan debiteurnummer', 'bg-danger']);
      }
    },
    async fetchUnit4Addresses() {
      if (!this.debtorCode) return;
      this.loadingUnit4Addresses = true;
      this.unit4FetchError = null;
      try {
        const result = await this.orderService.fetchUnit4DebtorAddresses(this.debtorCode, this.order.id);
        if (!result?.success) {
          const errorsText = result?.errors?.length
            ? ': ' + result.errors.map(e => e.message).join(', ')
            : '';
          this.$emit('showToast', [(result?.message || 'Debiteur niet gevonden in Unit4') + errorsText, 'bg-danger']);
          return;
        }
        const { invoiceAddressCode, deliveryAddresses, organisationName, perLineCandidates } = result.data;
        if (organisationName) {
          this.unit4OrganisationName = organisationName;
          if (this.order?.customer) {
            this.order.customer.organisation_name = organisationName;
          }
        }

        // Factuuradrescode invullen
        if (invoiceAddressCode) {
          this.invoiceAddressCode = invoiceAddressCode;
          this.order.output.invoiceAddressCode = invoiceAddressCode;
        }

        // Candidates vullen op elke orderregel:
        // - this.orderlines (UI state) krijgt de volledige lijst zodat de gebruiker via het zoekveld
        //   in de dropdown door alle ~1000 adressen kan bladeren.
        // - this.order.output.lines (gepersisteerd in DB) krijgt alleen de top-matches (score >= 50)
        //   zodat het output-blob compact blijft (~paar KB i.p.v. 1.4 MB) en bij heropen van de pagina
        //   de meest waarschijnlijke kandidaten direct zichtbaar zijn zonder verse fetch.
        //   Voor de volledige lijst klikt de gebruiker opnieuw op "Unit4 ophalen".
        const TOP_MATCH_THRESHOLD = 50;
        const candidatesForLine = (idx) =>
          (perLineCandidates && perLineCandidates[idx]) || deliveryAddresses;
        const topMatchesOnly = (list) =>
          (list || []).filter(c => typeof c.score === 'number' && c.score >= TOP_MATCH_THRESHOLD);

        if (deliveryAddresses?.length > 0 && this.orderlines?.length > 0) {
          this.orderlines = this.orderlines.map((line, idx) => ({
            ...line,
            deliveryAddress: {
              ...(line.deliveryAddress || {}),
              candidates: candidatesForLine(idx),
            }
          }));
          if (this.order?.output?.lines) {
            this.order.output.lines = this.order.output.lines.map((line, idx) => ({
              ...line,
              deliveryAddress: {
                ...(line.deliveryAddress || {}),
                candidates: topMatchesOnly(candidatesForLine(idx)),
              }
            }));
          }
        }

        this.$emit('showToast', [`Unit4 gegevens opgehaald (${deliveryAddresses?.length || 0} adressen)`, 'bg-success']);
        this.commitChange();
      } catch (error) {
        console.error('Fout bij ophalen Unit4 adressen:', error);
        this.$emit('showToast', ['Fout bij ophalen Unit4 gegevens', 'bg-danger']);
      } finally {
        this.loadingUnit4Addresses = false;
      }
    },
    formatInput() {
      if (!this.order?.input) {
        this.formattedInput = 'Geen input beschikbaar';
        this.inputType = 'text';
        return;
      }

      const input = this.order?.input;
      if (typeof input === 'string') {
        // Kijk of het XML, HTML of gewone tekst is
        const trimmedInput = input.trim();
        if (trimmedInput.startsWith('<') && (trimmedInput.startsWith('<?xml') || !trimmedInput.toLowerCase().includes('<html>'))) {
          // Het lijkt XML te zijn (maar geen HTML)
          this.formatXml(input);
        } else if (trimmedInput.toLowerCase().includes('<html>')) {
          // Het is HTML, behandel als tekst
          this.formattedInput = input;
          this.inputType = 'html';
        } else {
          // Probeer het als JSON te parsen
          this.formatJson(input);
        }
      } else {
        // Object, behandel als JSON
        this.formatJson(input);
      }
    },
    formatXml(xml) {
      try {
        const parser = new XMLParser({ ignoreAttributes: false });
        // We parsen de XML om te controleren of het geldige XML is
        parser.parse(xml);
        // Als het parsen succesvol is, gebruiken we onze prettyPrintXml functie
        this.formattedInput = this.prettyPrintXml(xml);
        this.inputType = 'xml';
      } catch (err) {
        console.error('Fout bij het parsen van XML:', err);
        this.formattedInput = xml;
        this.inputType = 'xml';
      }
    },
    formatJson(json) { 
      try {
        const parsedJson = typeof json === 'string' ? JSON.parse(json) : json;
        this.formattedInput = JSON.stringify(parsedJson, null, 2);
        this.inputType = 'json';
      } catch (error) {
        console.error('Fout bij het formatteren van JSON:', error);
        this.formattedInput = typeof json === 'string' ? json : JSON.stringify(json, null, 2);
        this.inputType = 'json';
      } 
    },
    formatDateForDisplay(dateStr) {
      if (!dateStr) return '';
      return Utils.getCustomFormattedDate(dateStr, { day: 'numeric', month: 'short', year: 'numeric' }) || '';
    },
    formatDateForInput(dateStr) {
      if (!dateStr) return '';
      return dateStr.substring(0, 10);
    },
    orderDateChanged(value) {
      if (value) {
        this.order.output.orderDate = value + 'T00:00:00Z';
        this.commitChange();
      }
    },
    deliveryDateChanged(value, index) {
      if (value) {
        const formatted = value + 'T00:00:00Z';
        this.orderlines[index].deliveryDateCustomer = formatted;
        this.order.output.lines[index].deliveryDateCustomer = formatted;
        this.commitChange();
      }
    },
    formatCurrency(amount, showCurrency = false) {
      return Utils.formatCurrency(amount, 'EUR', showCurrency);
    },
    prettyPrintXml(xml) {
      let formatted = '';
      let indent = '';
      const tab = '  '; // 2 spaties voor indentatie
      xml.split(/>\s*</).forEach(function(node) {
        if (node.match( /^\/\w/ )) {
          indent = indent.substring(tab.length); // Verminder indentatie
        }
        formatted += indent + '<' + node + '>\r\n';
        if (node.match( /^<?\w[^>]*[^/]$/ )) { // Verwijderd de backslash voor de forward slash
          indent += tab; // Verhoog indentatie
        }
      });
      return formatted.substring(1, formatted.length-3).trim();
    },
    resetOrderLines() {
      this.order.output.lines = JSON.parse(JSON.stringify(this.originalOutputLines));
      this.orderlines = this.order.output.lines;
      this.commitChange();
    },
    deleteOrderLine(index) {
      if (!window.confirm(`Weet je zeker dat je orderregel ${this.orderlines[index].orderLineNumber} wilt verwijderen?`)) return;
      this.order.output.lines.splice(index, 1);
      this.orderlines = this.order.output.lines;
      this.commitChange();
    },
    buildNewOrderLine(orderLineNumber) {
      // Neem datum + afleveradres(-candidates) over van de laatste regel, zodat
      // het datumveld zichtbaar is (v-if="orderline.deliveryDateCustomer") en de
      // adres-dropdown direct dezelfde keuzes toont.
      const lastLine = this.orderlines.length > 0
        ? this.orderlines[this.orderlines.length - 1]
        : null;

      const deliveryDateCustomer =
        lastLine?.deliveryDateCustomer ||
        this.order?.output?.orderDate ||
        (new Date().toISOString().slice(0, 10) + 'T00:00:00Z');

      // deliveryAddress en productIdentifier ALTIJD als object (nooit null), zodat
      // addressCodeChanged/productCodeChanged/customerPartIdChanged direct werken.
      const deliveryAddress = {
        id: lastLine?.deliveryAddress?.id || '',
        candidates: lastLine?.deliveryAddress?.candidates
          ? JSON.parse(JSON.stringify(lastLine.deliveryAddress.candidates))
          : [],
        inputAddress: '',
        selectedAddress: '',
      };

      return {
        orderLineNumber,
        productIdentifier: { productCode: '', customerPartId: '', productCandidates: [] },
        unitCode: lastLine?.unitCode || 'STK',
        qtyOrdered: 1,
        unitPrice: 0,
        linePrice: 0,
        nettNettLineAmount: 0,
        deliveryDateCustomer,
        deliveryAddress,
        // Unit4-only velden (frontend leest ze niet). Vaste waardes conform de
        // converters (BACKOR / true). originalPurchaseOrderLine wordt bewust NIET
        // gezet: een handmatig toegevoegde regel heeft geen originele PO-regel.
        stockProblem: 'BACKOR',
        correctDateCustomer: true,
      };
    },
    addOrderLine() {
      if (!this.order?.output) return;
      if (!Array.isArray(this.order.output.lines)) {
        this.order.output.lines = [];
        this.orderlines = this.order.output.lines;
      }

      // Nieuw nummer = max(bestaande orderLineNumbers) + 1 (fallback 1). Bestaande
      // nummers blijven ongemoeid; orderLineNumber gaat as-is naar Unit4 en wordt
      // als matching-sleutel gebruikt, dus het moet uniek zijn.
      const existingNumbers = this.orderlines
        .map(l => Number(l?.orderLineNumber))
        .filter(n => Number.isFinite(n));
      const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

      this.order.output.lines.push(this.buildNewOrderLine(nextNumber));
      this.orderlines = this.order.output.lines;

      // showCustomerPartId parallelle array aanvullen voor de nieuwe index.
      const updatedFlags = [...this.showCustomerPartId];
      updatedFlags[this.orderlines.length - 1] = false;
      this.showCustomerPartId = updatedFlags;

      this.commitChange();

      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.orderlines-list .orderline-item');
        items[items.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    unlockOrder() {
      const ok = window.confirm('Weet je zeker dat je deze order wilt ontgrendelen voor handmatige bewerking?');
      if (!ok) return;
      this.manuallyUnlocked = true;
      this.$emit('showToast', ['Order ontgrendeld — wijzigingen kunnen handmatig ingestuurd worden', 'bg-warning']);
    },
    async deleteOrder() {
      const confirm = window.confirm('Weet je zeker dat je deze order wilt verwijderen?');
      //check if to be sure
      if (confirm) {
        const result = await this.orderService.deleteOrder(this.order.id);
        console.log('result:', result);
        if (result.success) {
          this.$router.push('/orders');
        } else {
          this.$emit('showToast', ['Er is een fout opgetreden bij het verwijderen van de order', 'bg-danger']);
        }
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => this.$emit("showToast", ["Gekopieerd naar klembord", "bg-success"]))
        .catch(err => this.$emit("showToast", [`Fout bij kopiëren: ${err.message}`, "bg-danger"]));
    },
    copyJsonToClipboard() {
      const jsonContent = this.$refs.editableJsonView.jsonContent;
      navigator.clipboard.writeText(jsonContent)
        .then(() => this.$emit("showToast", ["Gekopieerd naar klembord", "bg-success"]))
        .catch(err => this.$emit("showToast", [`Fout bij kopiëren: ${err.message}`, "bg-danger"]));
    },
    copyObjectToClipboard() {
      const objectJson = JSON.stringify(this.order.output, null, 2);
      navigator.clipboard.writeText(objectJson)
        .then(() => this.$emit("showToast", ["Gekopieerd naar klembord", "bg-success"]))
        .catch(err => this.$emit("showToast", [`Fout bij kopiëren: ${err.message}`, "bg-danger"]));
    },
    showDuplicateWarning() {
      this.duplicateModal = new window.bootstrap.Modal(document.getElementById('duplicateOrderModal'));
      this.duplicateModal.show();
    },
    async proceedWithRetry() {
      if (this.pendingRetryOperation === 'json') {
        await this.performRetryWithJson();
      } else if (this.pendingRetryOperation === 'xml') {
        await this.performRetryWithOriginalXml();
      }
      
      if (this.duplicateModal) {
        this.duplicateModal.hide();
      }
      this.pendingRetryOperation = null;
      this.duplicateInfo = null;
    }
  },
  watch: {
    // Watch voor wijzigingen in het id prop (wanneer er naar een andere order wordt genavigeerd)
    id: {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          console.log(`Navigating to order ${newId} from ${oldId}`);
          this.loadOrderDetail();
          // Reset PDF state when navigating to new order
          if (this.pdfBlobUrl) {
            URL.revokeObjectURL(this.pdfBlobUrl);
            this.pdfBlobUrl = null;
          }
        }
      },
      immediate: false // We willen niet dat dit wordt uitgevoerd bij de eerste mount
    },
    // Load PDF when viewer is shown and order has PDF
    showPdfViewer: {
      handler(show) {
        if (show && this.hasPdfFile && !this.pdfBlobUrl) {
          this.loadPdf();
        }
      }
    },
    // Also load PDF when order changes and viewer is open
    'order.pdf_filename': {
      handler(filename) {
        if (filename && this.showPdfViewer && !this.pdfBlobUrl) {
          this.loadPdf();
        }
      }
    }
  },
  mounted() {
    this.loadOrderDetail().then(() => {
      // Load PDF after order is loaded if viewer is shown
      if (this.showPdfViewer && this.hasPdfFile) {
        this.loadPdf();
      }
    });
    this.service = new OAuth2AzureService();
    
    // Load saved PDF panel width
    const savedWidth = localStorage.getItem('orderPdfPanelWidth');
    if (savedWidth) {
      this.pdfPanelWidth = Math.min(Math.max(350, parseInt(savedWidth)), window.innerWidth * 0.7);
    }
    
    // Initialiseer tooltips
    this.$nextTick(() => {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    });
  },
  beforeUnmount() {
    // Cleanup PDF blob URL to prevent memory leaks
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
    // Cleanup resize listeners if still active
    if (this.isResizingPdf) {
      this.stopPdfResize();
    }
    // Flush pending auto-save zodat laatste wijziging niet verloren gaat
    this.flushPendingAutoSave();
  },
  async beforeRouteLeave(to, from, next) {
    await this.flushPendingAutoSave();
    next();
  },
};
</script>

<style scoped> 
/* Toegevoegde stijlen voor de debiteurproduct sectie */
.invisible-label {
  height: 30px; /* Zelfde hoogte als een label */
  margin-bottom: 0.5rem; /* Consistent met andere labels */
  display: block;
}

.edit-field-wrapper {
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  min-height: 38px; /* Minimale hoogte van input velden */
  display: flex;
  align-items: center;
  overflow: hidden; /* Verberg overflow tijdens de animatie */
  max-width: 12em; /* Maximale breedte van het veld */
  transform-origin: left center; /* Zorg dat de animatie vanaf links start */
  margin-right: 0; /* Standaard geen marge rechts */
}

/* Stijl voor het veld als het zichtbaar is */
.field-expanded {
  width: 12em !important; /* Forceer de breedte bij geopende staat */
  margin-right: 0.5rem !important; /* Voeg wat ruimte toe rechts */
  opacity: 1;
}

.card {
  border: none;
}
.custom-border-radius {
  border-radius: 0 0 7px 7px;
}
.no-top-border {
  border-top: none !important;
}
.orderlines-list {
  display: flex;
  flex-direction: column;
}
.orderlines-header {
  display: flex;
  flex-direction: column; 
}
.orderline-item {
  padding: 0.5em 0;
}
.custom-row {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1em;
  margin-bottom: 1em;
}
.orderline-item .row {
  margin-bottom: 0.5em;
}
@media (max-width: 767px) {
  .orderline-item .row > div {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
/* Style for disabled select options if needed */
select option:disabled {
  color: #999;
  font-style: italic;
}
.small-text {
  font-size: 0.875rem; /* Blijft hetzelfde als eerder */
  line-height: 1.2;
}

.small-text ul {
  padding-left: 1rem;
}

.small-text .line-errors {
  font-size: 0.625rem; /* Nog kleiner dan de hoofdtekst */
}

.small-text .line-errors li {
  margin-bottom: 0.2rem;
}

textarea {
  font-family: monospace;
  font-size: 14px;
}
.input-pre {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 15px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 500px;
  overflow-y: auto;
}
.input-pre code {
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  display: block;
  padding-left: 0;
  margin-left: -15px;
}
.input-pre code.xml {
  color: #0000ff;
}
.input-pre code.json {
  color: #006400;
}
.input-address-display {
  font-size: 0.875rem;
  color: #6c757d;
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.input-address-display-full {
  font-size: 0.875rem;
  color: #6c757d;
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  max-width: 250px;
  white-space: normal; /* Sta tekstomloop toe */
  overflow: visible; /* Toon alle tekst */
  cursor: default; /* Standaard muiscursor */
  align-self: flex-start; /* Uitlijnen aan de bovenkant */
}

.product-description-text {
  font-size: 0.8rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 600px;
}

.quantity-input {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* PDF Side-by-side Layout */
.order-detail-layout {
  display: flex;
  height: calc(100vh - 140px);
  width: 100%;
  overflow: hidden;
}

.order-detail-content {
  flex: 1;
  overflow-y: auto;
  min-width: 0;
}

/* PDF Panel - matching info-centrum style */
.pdf-panel {
  display: flex;
  flex: 0 0 auto;
  width: v-bind(pdfPanelWidth + 'px');
  height: 100%;
  min-height: 0;
}

.pdf-resize-handle {
  width: 10px;
  flex: 0 0 10px;
  background: #dee2e6;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.pdf-resize-handle:hover {
  background: #0d6efd;
}

.pdf-resize-handle:hover .resize-indicator {
  background: white;
}

.resize-indicator {
  width: 4px;
  height: 64px;
  background: #adb5bd;
  border-radius: 2px;
  transition: background 0.2s;
}

.pdf-panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: white;
  border-left: 1px solid #dee2e6;
}

.pdf-panel-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.pdf-iframe-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

/* Overlay to prevent iframe from stealing mouse events during resize */
.pdf-resize-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  cursor: ew-resize;
}

.pdf-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* Slide panel animation */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: all 0.3s ease;
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  width: 0 !important;
  opacity: 0;
}

.stock-cell {
  min-height: 38px;
}
.stock-badge {
  background-color: #f1f3f5;
  color: #212529;
  font-weight: 500;
  font-size: 0.85rem;
  padding: 0.4em 0.6em;
}
.spin {
  animation: spin 0.8s linear infinite;
  display: inline-block;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>




